import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Search from "@mui/icons-material/Search";

import agent from "../../utils/axios";
import { formatDate, formatMoney } from "../../utils/format";
import {
  billChipMeta,
  Callout,
  PayeeCell,
  paymentStatusMeta,
  StatusChip,
} from "../../components/ui";
import { lastPageIndex } from "../../utils/paging";
import { errorMessageOf } from "./billHelpers";

type RowType = {
  payeeId: string;
  payeeName: string;
  invoiceId: string | null;
  invoiceDate: string | null;
  billRefCode: string;
  billStatus: string;
  billAmount: number;
  paymentStatus: string;
  paymentMethod: string | null;
  notes?: string | null;
  name?: string | null;
};

type Order = "asc" | "desc";

// The list's payee id is projected as `_id`, so that is the field the ID sort must name.
const PAYEE_ID_SORT = "_id";

type SortableColumn = {
  id: string;
  label: string;
  align?: "left" | "right";
};

// Every header stays sortable. Payee covers both Payee Name and Payee ID.
const columns: SortableColumn[] = [
  { id: "invoiceId", label: "Invoice" },
  { id: "invoiceDate", label: "Invoice date" },
  { id: "billStatus", label: "Bill status" },
  { id: "paymentStatus", label: "Payment status" },
  // The list projects the amount as billAmount.
  { id: "billAmount", label: "Amount", align: "right" },
  { id: "paymentMethod", label: "Method" },
  { id: "notes", label: "Notes" },
];

const COLUMN_COUNT = columns.length + 2; // payee, view

const SEARCH_DEBOUNCE_MS = 300;

interface AllBillsProps {
  /** Called with the total number of bills after each load (the tab badge). */
  onCount?: (count: number) => void;
}

const AllBills = ({ onCount }: AllBillsProps) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState(PAYEE_ID_SORT);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [data, setData] = useState<RowType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    if (searchInput === searchTerm) return;
    const handle = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput, searchTerm]);

  const loadBills = useCallback(async () => {
    const requestId = ++requestRef.current;
    let movedPage = false;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await agent.Bills.getAllBills(
        page,
        rowsPerPage,
        orderBy,
        order,
        searchTerm
      );
      // Ignore a response that a newer request (or leaving the tab) has replaced.
      if (!mountedRef.current || requestId !== requestRef.current) return;
      const rows: RowType[] = Array.isArray(response?.data)
        ? response.data
        : [];
      const count = Number(response?.count);
      if (Number.isFinite(count)) onCountRef.current?.(count);
      // The page emptied since it was chosen (fewer bills now): go to the last page that
      // still has bills. Changing the page loads it; keep the bar up until then.
      const lastPage = lastPageIndex(count, rowsPerPage);
      if (rows.length === 0 && Number.isFinite(count) && page > lastPage) {
        movedPage = true;
        setPage(lastPage);
        return;
      }
      setData(rows);
      setTotalCount(Number.isFinite(count) ? count : rows.length);
    } catch (error) {
      if (!mountedRef.current || requestId !== requestRef.current) return;
      setData([]);
      setTotalCount(0);
      setLoadError(errorMessageOf(error, "The bills could not be loaded."));
    } finally {
      if (
        !movedPage &&
        mountedRef.current &&
        requestId === requestRef.current
      ) {
        setLoading(false);
      }
    }
  }, [page, rowsPerPage, orderBy, order, searchTerm]);

  useEffect(() => {
    loadBills();
  }, [loadBills, reloadKey]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0); // Reset to the first page on sort change
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyMessage = searchTerm
    ? `No bills match "${searchTerm}".`
    : "No bills yet.";

  return (
    <Card>
      <Box sx={{ p: 4 }}>
        <TextField
          size="small"
          label="Search bills"
          fullWidth
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search by Payee ID, Name, InvoiceId"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loadError && (
        <Box sx={{ px: 4, pb: 3 }}>
          <Callout
            tone="err"
            role="alert"
            title="The bills could not be loaded"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setReloadKey((key) => key + 1)}
              >
                Retry
              </Button>
            }
          >
            {loadError}
          </Callout>
        </Box>
      )}

      {/* Reserve the bar's height so the table doesn't jump while loading. */}
      <Box sx={{ height: 4 }}>
        {loading && <LinearProgress aria-label="Loading bills" />}
      </Box>

      <TableContainer>
        <Table aria-label="All bills">
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={
                  orderBy === "payeeName" || orderBy === PAYEE_ID_SORT
                    ? order
                    : false
                }
              >
                <TableSortLabel
                  active={orderBy === "payeeName"}
                  direction={orderBy === "payeeName" ? order : "asc"}
                  onClick={() => handleRequestSort("payeeName")}
                >
                  Payee
                </TableSortLabel>
                <TableSortLabel
                  active={orderBy === PAYEE_ID_SORT}
                  direction={orderBy === PAYEE_ID_SORT ? order : "asc"}
                  onClick={() => handleRequestSort(PAYEE_ID_SORT)}
                  aria-label="Sort by payee ID"
                  sx={{ ml: 2 }}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align ?? "left"}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleRequestSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => {
              const payeeName = (row.payeeName ?? "").trim();
              const notes = (row.notes ?? "").trim();
              return (
                <TableRow key={`${row.billRefCode || "no-ref"}-${index}`} hover>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <PayeeCell
                      name={payeeName || "Unnamed payee"}
                      id={row.payeeId}
                      to={
                        row.payeeId
                          ? `/payees/detail/${row.payeeId}`
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>{row.invoiceId || "—"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatDate(row.invoiceDate)}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      size="small"
                      meta={billChipMeta(row.billStatus, row.paymentStatus)}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      size="small"
                      meta={paymentStatusMeta(row.paymentStatus)}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatMoney(row.billAmount)}
                  </TableCell>
                  <TableCell>
                    {row.paymentMethod ? (
                      <StatusChip
                        size="small"
                        tone="neutral"
                        label={row.paymentMethod}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    {notes ? (
                      <Tooltip title={notes}>
                        <Box
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {notes}
                        </Box>
                      </Tooltip>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/bills/detail/${row.billRefCode}`}
                      disabled={!row?.billRefCode}
                      endIcon={<ChevronRight />}
                      aria-label={
                        row.billRefCode
                          ? `View bill ${row.billRefCode}`
                          : "No bill ref code to view"
                      }
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}

            {data.length === 0 && !loadError && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  align="center"
                  sx={{ py: 10, color: "text.secondary" }}
                >
                  {loading ? "Loading bills…" : emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[50, 100, 250]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Card>
  );
};

export default AllBills;
